import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { Image } from '@tiptap/extension-image'
import { Link } from '@tiptap/extension-link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered, 
  Quote, 
  Table as TableIcon, 
  Image as ImageIcon,
  Upload,
  Plus,
  Minus,
  Trash2,
  Heading1,
  Heading2,
  Heading3,
  Type,
  Link as LinkIcon,
  Unlink,
  Edit
} from 'lucide-react'
import { useRef, useState } from 'react'
import { isFileTypeAllowed, isFileSizeAcceptable, sanitizeFilename } from '@/lib/sanitizer'

interface RichTextEditorProps {
  content: string
  onChange: (content: string) => void
  placeholder?: string
}

const RichTextEditor = ({ content, onChange, placeholder }: RichTextEditorProps) => {
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkText, setLinkText] = useState('')
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false)
  const [attachmentDisplayText, setAttachmentDisplayText] = useState('')
  const [pendingAttachmentUrl, setPendingAttachmentUrl] = useState('')

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline hover:text-primary/80 cursor-pointer',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg shadow-sm border my-2 block',
        },
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[200px] p-4 border rounded-md [&_table]:border-collapse [&_table]:border [&_table]:border-border [&_td]:border [&_td]:border-border [&_td]:p-2 [&_th]:border [&_th]:border-border [&_th]:p-2 [&_th]:bg-muted/50 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-6 [&_h1]:leading-tight [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mb-3 [&_h2]:mt-5 [&_h2]:leading-tight [&_h3]:text-lg [&_h3]:font-medium [&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:leading-tight [&_p]:mb-3 [&_p]:leading-relaxed',
      },
    },
  })

  const handleFileUpload = async (file: File, sanitizedName?: string) => {
    try {
      // Use secure upload edge function for enhanced security
      const formData = new FormData()
      formData.append('file', file)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Authentication required')
      }

      // Use the correct Supabase URL instead of env variable
      const response = await fetch(`https://nbdkpnrohpqqqqqnmhit.supabase.co/functions/v1/secure-file-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData
      })

      const result = await response.json()
      
      console.log('Upload response:', result) // Debug log

      if (!response.ok) {
        console.error('Upload failed:', result)
        throw new Error(result.error || 'Upload failed')
      }

      console.log('Upload successful, URL:', result.url) // Debug log
      return result.url
    } catch (error) {
      console.error('Error uploading file:', error)
      toast({
        title: "업로드 실패",
        description: error instanceof Error ? error.message : "파일 업로드에 실패했습니다.",
        variant: "destructive"
      })
      return null
    }
  }

  const handleImageUpload = async () => {
    if (!fileInputRef.current) return
    fileInputRef.current.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    console.log('File selected:', file.name, file.type, file.size) // Debug log

    // Security validation
    if (!isFileTypeAllowed(file.name)) {
      console.log('File type not allowed:', file.type) // Debug log
      toast({
        title: "파일 형식 오류",
        description: "허용되지 않는 파일 형식입니다. 이미지, PDF, 문서 파일만 업로드 가능합니다.",
        variant: "destructive"
      });
      return;
    }

    if (!isFileSizeAcceptable(file.size)) {
      console.log('File size too large:', file.size) // Debug log
      toast({
        title: "파일 크기 오류", 
        description: "파일 크기가 10MB를 초과합니다.",
        variant: "destructive"
      });
      return;
    }

    console.log('File validation passed, starting upload...') // Debug log

    // Sanitize filename for security
    const sanitizedName = sanitizeFilename(file.name);
    console.log('Sanitized filename:', sanitizedName) // Debug log

    if (file.type.startsWith('image/')) {
      console.log('Uploading image file...') // Debug log
      const url = await handleFileUpload(file, sanitizedName)
      console.log('Upload result for image:', url) // Debug log
      if (url && editor) {
        console.log('Inserting image into editor with URL:', url) // Debug log
        editor.chain().focus().setImage({ src: url }).run()
        toast({
          title: "업로드 성공",
          description: "이미지가 업로드되었습니다."
        })
      }
    } else {
      console.log('Uploading non-image file...') // Debug log
      const url = await handleFileUpload(file, sanitizedName)
      console.log('Upload result for file:', url) // Debug log
      if (url && editor) {
        // Show dialog to customize attachment display text
        setPendingAttachmentUrl(url)
        setAttachmentDisplayText(sanitizedName)
        setAttachmentDialogOpen(true)
      }
    }
  }

  const insertTable = () => {
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
  }

  const addColumnBefore = () => {
    editor?.chain().focus().addColumnBefore().run()
  }

  const addColumnAfter = () => {
    editor?.chain().focus().addColumnAfter().run()
  }

  const deleteColumn = () => {
    editor?.chain().focus().deleteColumn().run()
  }

  const addRowBefore = () => {
    editor?.chain().focus().addRowBefore().run()
  }

  const addRowAfter = () => {
    editor?.chain().focus().addRowAfter().run()
  }

  const deleteRow = () => {
    editor?.chain().focus().deleteRow().run()
  }

  const deleteTable = () => {
    editor?.chain().focus().deleteTable().run()
  }

  const openLinkDialog = () => {
    const previousUrl = editor?.getAttributes('link').href || ''
    const selectedText = editor?.state.doc.textBetween(
      editor.state.selection.from,
      editor.state.selection.to
    ) || ''
    
    setLinkUrl(previousUrl)
    setLinkText(selectedText)
    setLinkDialogOpen(true)
  }

  const setLink = () => {
    if (!linkUrl) {
      editor?.chain().focus().unsetLink().run()
      setLinkDialogOpen(false)
      return
    }

    const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`
    
    if (linkText && !editor?.state.selection.empty) {
      // Replace selected text with custom text and link
      editor?.chain().focus().deleteSelection().insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary underline hover:text-primary/80">${linkText}</a>`).run()
    } else if (linkText) {
      // Insert new link with custom text
      editor?.chain().focus().insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary underline hover:text-primary/80">${linkText}</a>`).run()
    } else {
      // Just set link on selected text
      editor?.chain().focus().setLink({ href: url }).run()
    }
    
    setLinkDialogOpen(false)
    setLinkUrl('')
    setLinkText('')
  }

  const unsetLink = () => {
    editor?.chain().focus().unsetLink().run()
  }

  const insertAttachment = () => {
    if (pendingAttachmentUrl && attachmentDisplayText && editor) {
      editor.chain().focus().insertContent(`<a href="${pendingAttachmentUrl}" target="_blank" rel="noopener noreferrer" class="text-primary underline hover:text-primary/80">${attachmentDisplayText}</a>`).run()
      toast({
        title: "업로드 성공",
        description: "파일이 업로드되었습니다."
      })
    }
    setAttachmentDialogOpen(false)
    setPendingAttachmentUrl('')
    setAttachmentDisplayText('')
  }

  if (!editor) {
    return null
  }

  return (
    <div className="border rounded-lg">
      {/* Toolbar */}
      <div className="border-b p-2 flex flex-wrap gap-1">
        {/* Text Formatting */}
        <div className="flex gap-1">
          <Button
            type="button"
            variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            title="제목 1"
          >
            <Heading1 className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            title="제목 2"
          >
            <Heading2 className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            title="소제목"
          >
            <Heading3 className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('paragraph') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().setParagraph().run()}
            title="본문"
          >
            <Type className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="h-4 w-px bg-border mx-1" />
        
        {/* Text Style */}
        <div className="flex gap-1">
          <Button
            type="button"
            variant={editor.isActive('bold') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            title="굵게"
          >
            <Bold className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('italic') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title="기울임"
          >
            <Italic className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="h-4 w-px bg-border mx-1" />
        
        {/* Link */}
        <div className="flex gap-1">
          <Button
            type="button"
            variant={editor.isActive('link') ? 'default' : 'ghost'}
            size="sm"
            onClick={openLinkDialog}
            title="링크 추가/수정"
          >
            <LinkIcon className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={unsetLink}
            disabled={!editor.isActive('link')}
            title="링크 제거"
          >
            <Unlink className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="h-4 w-px bg-border mx-1" />
        
        {/* Lists and Quote */}
        <div className="flex gap-1">
          <Button
            type="button"
            variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title="글머리 기호"
          >
            <List className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title="번호 매기기"
          >
            <ListOrdered className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('blockquote') ? 'default' : 'ghost'}
            size="sm"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            title="인용문"
          >
            <Quote className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="h-4 w-px bg-border mx-1" />
        
        {/* Table and Media */}
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={insertTable}
            title="표 삽입"
          >
            <TableIcon className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleImageUpload}
            title="이미지/파일 업로드"
          >
            <ImageIcon className="w-4 h-4" />
          </Button>
        </div>
         
        {/* Table editing controls - only show when cursor is in a table */}
        {editor.isActive('table') && (
          <>
            <div className="h-4 w-px bg-border mx-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addColumnBefore}
              title="열 앞에 추가"
            >
              <Plus className="w-3 h-3" />
              열추가
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addRowBefore}
              title="행 위에 추가"
            >
              <Plus className="w-3 h-3" />
              행추가
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={deleteColumn}
              title="열 삭제"
            >
              <Minus className="w-3 h-3" />
              열삭제
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={deleteRow}
              title="행 삭제"
            >
              <Minus className="w-3 h-3" />
              행삭제
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={deleteTable}
              title="테이블 삭제"
            >
              <Trash2 className="w-3 h-3" />
              테이블삭제
            </Button>
          </>
        )}
      </div>

      {/* Editor */}
      <EditorContent editor={editor} />

      {/* Hidden file input */}
      <Input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        onChange={handleFileChange}
      />

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>링크 추가</DialogTitle>
            <DialogDescription>
              링크 URL과 표시할 텍스트를 입력하세요.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="linkUrl">URL</Label>
              <Input
                id="linkUrl"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="linkText">표시 텍스트 (선택사항)</Label>
              <Input
                id="linkText"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="링크 텍스트"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>
              취소
            </Button>
            <Button onClick={setLink}>
              {editor?.isActive('link') ? '수정' : '추가'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attachment Display Text Dialog */}
      <Dialog open={attachmentDialogOpen} onOpenChange={setAttachmentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>첨부파일 표시 텍스트</DialogTitle>
            <DialogDescription>
              첨부파일에 표시될 텍스트를 입력하세요.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="attachmentText">표시 텍스트</Label>
              <Input
                id="attachmentText"
                value={attachmentDisplayText}
                onChange={(e) => setAttachmentDisplayText(e.target.value)}
                placeholder="첨부파일 이름"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachmentDialogOpen(false)}>
              취소
            </Button>
            <Button onClick={insertAttachment} disabled={!attachmentDisplayText.trim()}>
              추가
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default RichTextEditor